import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Button, Slide, 
    Typography,
    useTheme
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import {
    useCallback, useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import NumberInputScreen from '../components/numberInput';
import ProgressBar from '../components/progressBar';
import dataDogLogger from '../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyVariants, 
    TypographyWeights
} from '../../../figmaTheme';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number
};

export const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const DrinkBaselines2 = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinkBaselineAnswers = useMemo(() => {
        const answers = formContext.answers;
        return [
            answers.mon_baseline,
            answers.tue_baseline,
            answers.wed_baseline,
            answers.thu_baseline,
            answers.fri_baseline,
            answers.sat_baseline,
            answers.sun_baseline
        ];
    }, [
        formContext.answers.mon_baseline,
        formContext.answers.tue_baseline,
        formContext.answers.wed_baseline,
        formContext.answers.thu_baseline,
        formContext.answers.fri_baseline,
        formContext.answers.sat_baseline,
        formContext.answers.sun_baseline
    ]);
    const [dayHeight, setDayHeight] = useState(null);
    const [inputHeight, setInputHeight] = useState(null);
    const [textBoxHeight, setTextBoxHeight] = useState(null);
    const [titleHeight, setTitleHeight] = useState(null);
    const initialScreenWidth = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth;
        }
        return 0;
    }, []);

    const [screenWidth, setScreenWidth] = useState(initialScreenWidth);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflowY: 'scroll',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pointerEvents: 'none'
        },
        title: {
            position: 'relative',
            margin: '0 0 10px',
            [theme.breakpoints.up('lg')]: {
                textAlign: 'center'
            }
        },
        subtitle: {
            margin: '0 0 32px',
            [theme.breakpoints.up('lg')]: {
                textAlign: 'center'
            },
            opacity: 0,
            animation: '$fadeIn 0.5s forwards 0.5s'
        },
        navigation: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '8px',
            transition: 'transform 0.5s',
            pointerEvents: 'auto',
            opacity: 0,
            animation: '$fadeIn 0.5s forwards 0.5s'
        },
        navigation2: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '8px',
            transition: 'transform 0.5s, opacity 0.5s',
            pointerEvents: 'auto',
            position: 'absolute',
            height: '48px',
            opacity: 0,
            transform: `translate(0, ${textBoxHeight + inputHeight + 8}px)`
        },
        circleContainer: {
            display: 'flex',
            flexShrink: 0,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            rowGap: '8px',
            transition: 'transform 0.5s',
            pointerEvents: 'auto',
            height: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
            transform: 'rotate(-90deg)'
        },
        circleContainerRotate: {
            animation: '$verticalTransition 0.5s forwards 0.5s, $circleContainerShrink 0.5s forwards 2s'
        },
        circleContainerShift: {
            animation: '$circleContainerShift 0.5s forwards 2s, $fadeOut 0.5s forwards 4.5s'
        },
        letterContainer: {
            position: 'absolute',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            columnGap: '8px',
            transition: 'transform 0.5s',
            pointerEvents: 'auto',
        },
        error: {
            textAlign: 'center',
            color: theme.palette.error.main,
            margin: '8px 0 0'
        },
        leftArrow: {
            marginRight: 'auto',
        },
        rightArrow: {
            marginLeft: 'auto',
        },
        circle: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: theme.palette.secondary.main,
            transition: 'background-color 0.5s 1.2s, opacity 0.5s'
        },
        dayLetter: {
            fontWeight: TypographyWeights.BOLD,
            transition: 'opacity 0.5s'
        },
        shrink: {
            animation: '$changeShape 0.5s forwards 1.2s, $grow 0.5s forwards 2s'
        },
        inputBox: {
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto',
            marginBottom: '8px',
            opacity: 0,
            animation: '$fadeIn 0.5s forwards 0.5s'
        },
        hide: {
            opacity: 0,
            pointerEvents: 'none',
            animation: '$fadeOut 0.5s forwards 0s'
        },
        show: {
            opacity: 1,
            transition: 'opacity 0.5s'
        },
        hideText: {
            pointerEvents: 'none',
            animation: '$fadeOut 0.5s forwards 0s, $fadeIn 0.5s forwards 2.5s'
        },
        day: {
            fontWeight: TypographyWeights.MEDIUM,
            padding: '0 16px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            opacity: 0
        },
        dayText: {
            lineHeight: '40px'
        },
        fadeIn: {
            animation: '$fadeIn 0.5s forwards 2.5s'
        },
        summaryContainer: {
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '16px',
            width: 'calc(100% - 48px)',
            margin: '0 auto',
            transform: `translate(0, ${textBoxHeight + inputHeight + ((dayHeight + 1) * 7 - 1) / 2 + 67}px)`,
        },
        shiftUp: {
            animation: '$shiftUp 0.5s forwards 4.5s'
        },
        summaryBox: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            borderRadius: '32px 8px 8px 32px'
        },
        summaryText: {
            padding: '0 12px',
            backgroundColor: theme.palette.secondary['100'],
            lineHeight: '46px',
            opacity: 0,
            width: '100%',
            height: '100%',
            transform: 'translate(-100vw, 0)',
            marginLeft: '32px',
            paddingLeft: '56px',
            borderRadius: '32px 8px 8px 32px'
        },
        summaryValue: {
            backgroundColor: theme.palette.secondary.main,
            fontWeight: TypographyWeights.BOLD,
            color: theme.palette.white,
            borderRadius: '50%',
            border: `4px solid ${theme.palette.white}`,
            position: 'absolute',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
        },
        shiftDrinksText: {
            animation: '$shift 0.5s forwards 3.5s, $fadeIn 0.5s forwards 3.5s'
        },
        shiftDaysText: {
            animation: '$shift 0.5s forwards 3.5s, $fadeIn 0.5s forwards 3.5s'
        },
        fadeInDrinksValue: {
            animation: '$fadeIn 0.5s forwards 3s'
        },
        fadeInDaysValue: {
            animation: '$fadeIn 0.5s forwards 3s'
        },
        arrowButton: {
            padding: '16px',
            borderRadius: '50%',
            minWidth: 0,
            lineHeight: 0,
            flexShrink: 0
        },
        none: {
            backgroundColor: theme.palette.secondary.light
        },
        low: {
            backgroundColor: theme.palette.secondary['100']
        },
        moderate: {
            backgroundColor: theme.palette.secondary['200']
        },
        heavy: {
            backgroundColor: theme.palette.secondary['300']
        },
        hidden: {
            opacity: 0
        },
        tipBox: {
            maxWidth: '100%',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '2px',
            transform: 'translate(0, 100vh)',
            opacity: 0,
            height: '208px',
            animation: '$fadeIn 0.25s ease-in-out 4.75s forwards, $slideUp 0.5s forwards 4.5s'
        },
        tipBodyBox: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            backgroundColor: theme.palette.secondary['100'],
            padding: '16px',
            borderRadius: '8px',
        },
        tipBody1: {
            fontWeight: TypographyWeights.BOLD
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: 1 
            },
            '100%': {
                opacity: 0 
            },
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0 
            },
            '100%': {
                opacity: 1 
            },
        },
        '@keyframes verticalTransition': {
            '0%': {
                transform: 'rotate(-90deg)' 
            },
            '100%': {
                transform: 'rotate(0deg)' 
            },
        },
        '@keyframes changeShape': {
            '0%': {
                borderRadius: '50%' 
            },
            '100%': {
                borderRadius: 0 
            },
        },
        '@keyframes grow': {
            '0%': {
                width: '8px',
                height: '8px' 
            },
            '100%': {
                width: `${Math.min(screenWidth, 692) - 48}px`,
                height: dayHeight 
            },
        },
        '@keyframes circleContainerShrink': {
            '0%': {
                rowGap: '8px' 
            },
            '100%': {
                rowGap: '1px' 
            },
        },
        '@keyframes circleContainerShift': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: 'translate(0, 5px)' 
            },
        },
        '@keyframes shift': {
            '0%': {
                transform: 'translate(-100vw, 0)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
        '@keyframes shiftUp': {
            '0%': {
                transform: `translate(0, ${textBoxHeight + inputHeight + ((dayHeight + 1) * 7 - 1) / 2 + 67}px)` 
            },
            '100%': {
                transform: `translate(0, ${textBoxHeight - 43}px)` 
            },
        },
        '@keyframes slideUp': {
            '0%': {
                transform: 'translate(0, 100vh)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        }
    })), [dayHeight, screenWidth, titleHeight, textBoxHeight, inputHeight]);

    const classes = useStyles();
    const theme = useTheme();
    const [cookies] = useCookies();
    const [startTime, setStartTime] = useState(formContext.startTime);

    const inputRef = useRef(null);
    const [day, setDay] = useState(formContext.screenDirection === 'right' ? 6 : 0);
    const [drinkBaselines, setDrinkBaselines] = useState<number[]>(drinkBaselineAnswers || []);
    const [value, setValue] = useState(drinkBaselineAnswers[day]?.toString() || '');
    const [error, setError] = useState('');
    const [disablePreviousDay, setDisablePreviousDay] = useState(true);
    const [disableNextDay, setDisableNextDay] = useState(true);
    const [title, setTitle] = useState(`On a typical <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main};">${days[day]}</span>,<br />how many drinks do you usually have?`);
    const [subtitle, setSubtitle] = useState('Enter “0” if you don\'t usually drink on this day');
    const [hide, setHide] = useState(false);
    const [dayColors, setDayColors] = useState(days.map(() => theme.palette.secondary.light));
    const [opacity, setOpacity] = useState(1);
    const [slide, setSlide] = useState(true);
    const [direction, setDirection] = useState(formContext.screenDirection);
    const scrollableContainer = useRef(null);
    const mondayBaseline = useRef(null);
    const inputBox = useRef(null);
    const textBox = useRef(null);
    const titleElement = useRef(null);

    const onClickNextDay = () => {
        if (!value || error) return;
        setStartTime(new Date().getTime());
        const newDrinkBaselines = drinkBaselines;
        newDrinkBaselines[day] = parseInt(value);
        setDrinkBaselines(newDrinkBaselines);
        if (day === 6) {
            setHide(true);

            setDayColors(drinkBaselines.map((baseline) => {
                if (baseline === 0) return classes.none;
                if (baseline <= 3) return classes.low;
                if (baseline <= 6) return classes.moderate;
                else return classes.heavy;
            }));

            setTimeout(() => {
                setTitle('Based on your answers, here\'s what a typical week looks like for you today:');
                setSubtitle('');
            }, 2500);

            return;
        }

        setDay(day + 1);
        if (inputRef.current) inputRef.current.focus();
        setOpacity(0);

        setSlide(false);
        setDirection('right');
        if (scrollableContainer.current) scrollableContainer.current.scrollTo({
            top: 0,
            behaviour: 'smooth' 
        });
    };

    useEffect(() => {
        setTimeout(() => {
            setOpacity(1);
        }, 300);
    }, [opacity]);

    const onClickPreviousDay = () => {
        if (day === 0) return;
        setStartTime(null);
        const newDrinkBaselines = drinkBaselines;
        newDrinkBaselines[day] = parseInt(value);

        setDay(day - 1);
        if (inputRef.current) inputRef.current.focus();
        setOpacity(0);

        setSlide(false);
        setDirection('left');
        if (scrollableContainer.current) scrollableContainer.current.scrollTo({
            top: 0,
            behaviour: 'smooth' 
        });
    };

    useEffect(() => {
        setTimeout(() => {
            setTitle(`On a typical <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main};">${days[day]}</span>,<br />how many drinks do you usually have?`);
        }, 500);
    }, [slide]);

    useEffect(() => {
        setSlide(true);
        setDirection(direction === 'left' ? 'right' : 'left');
    }, [title]);

    const onChange = (e) => {
        if (!e.target.value) {
            setValue('');
            setError('');
            return;
        }

        if (parseInt(e.target.value) < 0) setError('Your drink number must be 0 or more');
        if (e.target.value.match(/\./g) && parseInt(e.target.value).toString() !== e.target.value) setError('Please round to nearest whole drink');
        else setError('');

        const value = e.target.value;
        let newValue = value;
        if (value.length > 1 && value[0] === '0') newValue = newValue.slice(1);
        setValue(newValue);
    };

    const onKeyPress = (e) => {
        if (!value || error) return;

        if (e.key === 'Enter') {
            if (day === 6 && inputRef.current) (inputRef.current as HTMLElement).blur();
            onClickNextDay();
        }
    };

    const onBackClick = () => {
        if (day === 0) {
            formContext.goBack();
            return;
        }
        if (!hide) onClickPreviousDay();
        setHide(false);
    };

    useEffect(() => {
        if (drinkBaselineAnswers.length) setDrinkBaselines(drinkBaselineAnswers);
    });

    useEffect(() => {
        setDisablePreviousDay(day === 0);
        setValue(typeof drinkBaselines[day] === 'number' ? drinkBaselines[day].toString() : '');
        formContext.fireTrackingEvents(formContext, `${days[day].slice(0, 3).toLowerCase()}_baseline`, cookies);
    }, [day]);

    useEffect(() => {
        if (!value || error) setDisableNextDay(true);
        else setDisableNextDay(false);
    }, [value]);

    useEffect(() => {
        setValue(drinkBaselineAnswers[day]?.toString() || '');
    }, [drinkBaselineAnswers]);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        dataDogLogger(
            'info',
            `Diagnostic screen ${days[day].slice(0, 3).toLowerCase()}_baseline rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url: `${days[day].slice(0, 3).toLowerCase()}_baseline`,
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    useEffect(() => {
        if (!mondayBaseline.current) return;

        setDayHeight(mondayBaseline.current.clientHeight);
    }, [mondayBaseline.current]);

    useEffect(() => {
        if (!inputBox.current) return;

        setInputHeight(inputBox.current.clientHeight);
    }, [inputBox.current]);

    useEffect(() => {
        if (!textBox.current) return;

        setTextBoxHeight(textBox.current.clientHeight);
    }, [textBox.current]);

    useEffect(() => {
        if (!titleElement.current) return;

        setTitleHeight(titleElement.current.clientHeight);
    }, [titleElement.current, title]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenWidth(window.innerWidth);
        };
        window.addEventListener('resize', resizeListener);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    const onClick = () => {
        const totalDrinksBaseline = drinkBaselines.reduce((acc, curr) => acc + curr, 0);
        const totalDryDaysBaseline = drinkBaselines.filter(drinks => drinks === 0).length;
        const answer = {
            totalDrinksBaseline,
            totalDryDaysBaseline 
        };
        drinkBaselines.forEach((baseline, index) => {
            answer[`${days[index].slice(0, 3).toLowerCase()}_baseline`] = baseline;
        });

        formContext.setAnswers(answer);
        formContext.goNext();
    };

    useEffect(() => {
        if (!inputRef.current) return;
 
        const timeout = setTimeout(() => {
            inputRef.current.focus();
        }, 1000);

        return () => clearTimeout(timeout);
    }, [inputRef.current]);

    const totalDrinks = useMemo(() => drinkBaselines.reduce((sum, baseline) => sum + baseline, 0), [drinkBaselines]);

    const getCoachNote = useCallback(() => {
        const heavyDrinkingDays = drinkBaselines.reduce((sum, baseline) => sum + (baseline > 4 ? 1 : 0), 0);
        const drinkingDays = 7 - drinkBaselines.reduce((sum, baseline) => sum + (baseline === 0 ? 1 : 0), 0);
        const averageDrinksPerDay = Math.ceil(drinkBaselines.reduce((sum, baseline) => sum + baseline, 0) / 7);
        const outlierDays = drinkBaselines.reduce((sum, baseline) => sum + (baseline >= averageDrinksPerDay + 2 ? 1 : 0), 0);

        let body1 = '';
        let body2 = '';
        let userPercentage = 89;

        if (totalDrinks >= 1 && totalDrinks <= 5) { userPercentage = 99; }
        else if (totalDrinks >= 6 && totalDrinks <= 10) { userPercentage = 89; }
        else if (totalDrinks >= 11 && totalDrinks <= 15) { userPercentage = 63; }
        else if (totalDrinks >= 16 && totalDrinks <= 20) { userPercentage = 54; }
        else { userPercentage = 50; }

        if (heavyDrinkingDays > 3 && drinkingDays === 7) {
            body1 = '57% of Sunnyside members join us as daily drinkers, so this is a pretty typical starting point.';
            body2 = 'We\'ll personalize your plan to reduce heavy drinking days and eventually add dry days to get you right where you want to be!';
        } else if (heavyDrinkingDays >= 1 && heavyDrinkingDays <= 3 && drinkingDays === 7) {
            body1 = '57% of Sunnyside members join us as daily drinkers with occasional heavier drinking days, so this is a pretty typical starting point.';
            body2 = 'We\'ll personalize your plan to reduce heavy drinking days and introduce dry days to get you right where you want to be!';
        } else if (heavyDrinkingDays === 0 && drinkingDays === 7) {
            body1 = '57% of Sunnyside members join us as daily drinkers, so this is a pretty typical starting point.';
            body2 = 'We\'ll personalize your plan to prioritize adding dry days each week, setting you up to get to where you want to be!';
        } else if (drinkingDays >= 4 && drinkingDays <= 6 && outlierDays >= 1) {
            body1 = `${drinkingDays === 4 ? '89' : drinkingDays === 5 ? '79' : '68'}% of Sunnyside members join us with ${drinkingDays} or more drinking days per week, with occasional heavier drinking days.`;
            body2 = 'This is a pretty typical starting point. We\'ll personalize your plan to reduce heavy drinking days and introduce dry days to get you right where you want to be!';
        } else if (drinkingDays >= 4 && drinkingDays <= 6 && outlierDays === 0) {
            body1 = `${drinkingDays === 4 ? '89' : drinkingDays === 5 ? '79' : '68'}% of Sunnyside members join us with ${drinkingDays} or more drinking days per week, so this is a pretty typical starting point.`;
            body2 = 'We\'ll personalize your plan to reduce heavy drinking days and eventually add dry days to get you right where you want to be!';
        } else if (drinkingDays >= 1 && drinkingDays <= 4 && outlierDays >= 1) {
            body1 = `${userPercentage}% of Sunnyside members join us at ${totalDrinks} or more total drinks per week, with occasional heavier drinking days.`;
            body2 = 'This is a pretty typical starting point. We\'ll personalize your plan to reduce heavy drinking days and overall drinks per week to get you right where you want to be!';
        } else if (drinkingDays >= 1 && drinkingDays <= 4 && outlierDays === 0) {
            body1 = `${userPercentage}% of Sunnyside members join us at ${totalDrinks} or more total drinks per week, so this is a pretty typical starting point.`;
            body2 = 'We\'ll personalize your plan to reduce overall drinks per week to get you right where you want to be!';
        } else if (drinkingDays === 0) {
            body1 = 'Starting from a place of zero drinks per week is an incredible accomplishment. You\'re already in a great place!';
            body2 = 'We\'ll work with you to maintain this pattern for the long-term, with the support of coaching and our incredible member community.';
        }

        return {
            body1,
            body2 
        };
    }, [drinkBaselines]);

    const {
        body1, body2 
    } = getCoachNote();

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={onBackClick}
            goNext={hide ? onClick : null}
            buttonDelay={2500}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position + day + (hide ? 1 : 0)}
                    sectionKey={'habits'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box
                        ref={scrollableContainer}
                        className={classes.content}
                    >
                        <Box
                            ref={textBox}
                            className={hide ? classes.hideText : null}
                            style={{
                                height: `${textBoxHeight}px` 
                            }}
                        >
                            <Slide
                                in={slide}
                                direction={direction}
                                timeout={250}
                                mountOnEnter
                            >
                                <Typography
                                    ref={titleElement}
                                    variant={TypographyVariants.H3}
                                    className={classes.title}
                                    dangerouslySetInnerHTML={{
                                        __html: title 
                                    }}
                                />
                            </Slide>
                            <Typography
                                variant={TypographyVariants.H6}
                                className={classes.subtitle}
                                dangerouslySetInnerHTML={{
                                    __html: subtitle 
                                }}
                            />
                        </Box>
                        <Box
                            className={`${classes.inputBox} ${hide ? classes.hide : null}`}
                            ref={inputBox}
                        >
                            <NumberInputScreen
                                sectionKey={'habits'}
                                inputRef={inputRef}
                                onChange={onChange}
                                onKeyPress={onKeyPress}
                                value={value}
                                maxWidth={97}
                            />
                        </Box>
                        <Box
                            className={classes.navigation2}
                            style={{
                                opacity: hide ? 1 : 0 
                            }}
                        >
                            <Box className={hide ? classes.circleContainerShift : null}>
                                <Box className={`${classes.circleContainer} ${hide ? classes.circleContainerRotate : null}`}>
                                    <Box className={`${classes.circle} ${hide ? classes.shrink : classes.hide} ${hide ? dayColors[0] : null}`}>
                                        <Box
                                            className={`${classes.day} ${hide ? classes.fadeIn : null}`}
                                            ref={mondayBaseline}
                                        >
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                            >
                                                {days[0]}
                                            </Typography>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                                style={{
                                                    fontWeight: TypographyWeights.BOLD 
                                                }}
                                            >
                                                {drinkBaselines[0] || 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.circle} ${hide ? classes.shrink : classes.hide} ${hide ? dayColors[1] : null}`}>
                                        <Box className={`${classes.day} ${hide ? classes.fadeIn : null}`}>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                            >
                                                {days[1]}
                                            </Typography>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                                style={{
                                                    fontWeight: TypographyWeights.BOLD 
                                                }}
                                            >
                                                {drinkBaselines[1] || 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.circle} ${hide ? classes.shrink : classes.hide} ${hide ? dayColors[2] : null}`}>
                                        <Box className={`${classes.day} ${hide ? classes.fadeIn : null}`}>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                            >
                                                {days[2]}
                                            </Typography>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                                style={{
                                                    fontWeight: TypographyWeights.BOLD 
                                                }}
                                            >
                                                {drinkBaselines[2] || 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.circle} ${hide ? classes.shrink : classes.hide} ${hide ? dayColors[3] : null}`}>
                                        <Box className={`${classes.day} ${hide ? classes.fadeIn : null}`}>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                            >
                                                {days[3]}
                                            </Typography>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                                style={{
                                                    fontWeight: TypographyWeights.BOLD 
                                                }}
                                            >
                                                {drinkBaselines[3] || 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.circle} ${hide ? classes.shrink : classes.hide} ${hide ? dayColors[4] : null}`}>
                                        <Box className={`${classes.day} ${hide ? classes.fadeIn : null}`}>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                            >
                                                {days[4]}
                                            </Typography>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                                style={{
                                                    fontWeight: TypographyWeights.BOLD 
                                                }}
                                            >
                                                {drinkBaselines[4] || 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.circle} ${hide ? classes.shrink : classes.hide} ${hide ? dayColors[5] : null}`}>
                                        <Box className={`${classes.day} ${hide ? classes.fadeIn : null}`}>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                            >
                                                {days[5]}
                                            </Typography>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                                style={{
                                                    fontWeight: TypographyWeights.BOLD 
                                                }}
                                            >
                                                {drinkBaselines[5] || 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.circle} ${hide ? classes.shrink : classes.hide} ${hide ? dayColors[6] : null}`}>
                                        <Box className={`${classes.day} ${hide ? classes.fadeIn : null}`}>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                            >
                                                {days[6]}
                                            </Typography>
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.dayText}
                                                style={{
                                                    fontWeight: TypographyWeights.BOLD 
                                                }}
                                            >
                                                {drinkBaselines[6] || 0}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box className={classes.navigation}>
                            <Button
                                className={`${classes.leftArrow} ${classes.arrowButton} ${hide ? classes.hide : null}`}
                                style={{
                                    backgroundColor: disablePreviousDay ? theme.palette.neutral['100'] : theme.palette.secondary.main,
                                    cursor: disablePreviousDay ? 'not-allowed' : 'pointer' 
                                }}
                                onClick={onClickPreviousDay}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7.79497 1.90498C8.06834 2.17834 8.06834 2.62156 7.79497 2.89493L3.38995 7.29995H14.3C14.6866 7.29995 15 7.61335 15 7.99995C15 8.38655 14.6866 8.69995 14.3 8.69995H3.38995L7.79497 13.105C8.06834 13.3783 8.06834 13.8216 7.79497 14.0949C7.52161 14.3683 7.07839 14.3683 6.80503 14.0949L1.20503 8.49493C0.931658 8.22156 0.931658 7.77834 1.20503 7.50498L6.80503 1.90498C7.07839 1.63161 7.52161 1.63161 7.79497 1.90498Z"
                                        fill={theme.palette.white}
                                    />
                                </svg>
                            </Button>
                            <Box className={classes.letterContainer}>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={`${classes.dayLetter} ${hide ? classes.hide : classes.show}`}
                                    style={{
                                        color: hide ? dayColors[0] : theme.palette.secondary.main,
                                        transform: 'translate(1px, 0)' 
                                    }}
                                >
                                    M
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={`${classes.dayLetter} ${hide ? classes.hide : classes.show}`}
                                    style={{
                                        color: hide ? dayColors[1] : day >= 1 ? theme.palette.secondary.main : theme.palette.secondary.light,
                                        transform: 'translate(-1px, 0)' 
                                    }}
                                >
                                    T
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={`${classes.dayLetter} ${hide ? classes.hide : classes.show}`}
                                    style={{
                                        color: hide ? dayColors[1] : day >= 2 ? theme.palette.secondary.main : theme.palette.secondary.light,
                                        transform: 'translate(-3px, 0)' 
                                    }}
                                >
                                    W
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={`${classes.dayLetter} ${hide ? classes.hide : classes.show}`}
                                    style={{
                                        color: hide ? dayColors[1] : day >= 3 ? theme.palette.secondary.main : theme.palette.secondary.light,
                                        transform: 'translate(-4px, 0)' 
                                    }}
                                >
                                    T
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={`${classes.dayLetter} ${hide ? classes.hide : classes.show}`}
                                    style={{
                                        color: hide ? dayColors[1] : day >= 4 ? theme.palette.secondary.main : theme.palette.secondary.light,
                                        transform: 'translate(-4px, 0)' 
                                    }}
                                >
                                    F
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={`${classes.dayLetter} ${hide ? classes.hide : classes.show}`}
                                    style={{
                                        color: hide ? dayColors[1] : day >= 5 ? theme.palette.secondary.main : theme.palette.secondary.light,
                                        transform: 'translate(-3px, 0)' 
                                    }}
                                >
                                    S
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.SMALL}
                                    className={`${classes.dayLetter} ${hide ? classes.hide : classes.show}`}
                                    style={{
                                        color: hide ? dayColors[1] : day >= 6 ? theme.palette.secondary.main : theme.palette.secondary.light,
                                        transform: 'translate(-2px, 0)' 
                                    }}
                                >
                                    S
                                </Typography>
                            </Box>
                            <Button
                                className={`${classes.rightArrow} ${classes.arrowButton} ${hide ? classes.hide : null}`}
                                style={{
                                    backgroundColor: disableNextDay ? theme.palette.neutral['100'] : theme.palette.secondary.main,
                                    cursor: disableNextDay ? 'not-allowed' : 'pointer' 
                                }}
                                onClick={onClickNextDay}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 17 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8.70503 14.095C8.43166 13.8216 8.43166 13.3784 8.70503 13.105L13.1101 8.69999L2.2 8.69999C1.8134 8.69999 1.5 8.38659 1.5 7.99999C1.5 7.61339 1.8134 7.29999 2.2 7.29999L13.1101 7.29999L8.70503 2.89496C8.43166 2.62159 8.43166 2.17838 8.70503 1.90501C8.97839 1.63164 9.42161 1.63164 9.69498 1.90501L15.295 7.50501C15.5683 7.77838 15.5683 8.22159 15.295 8.49496L9.69497 14.095C9.42161 14.3683 8.97839 14.3683 8.70503 14.095Z"
                                        fill={theme.palette.white}
                                    />
                                </svg>
                            </Button>
                        </Box>
                        <Box className={`${classes.summaryContainer} ${hide ? classes.shiftUp : null}`}>
                            <Box className={classes.summaryBox}>
                                <Typography
                                    variant={TypographyVariants.H5}
                                    className={`${classes.summaryText} ${hide ? classes.shiftDrinksText : null}`}
                                >
                                    {'Total Weekly '}
                                    <span
                                        style={{
                                            fontWeight: TypographyWeights.BOLD
                                        }}
                                    >
                                        Drinks
                                    </span>
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H2}
                                    className={`${classes.summaryValue} ${hide ? classes.fadeInDrinksValue : null}`}
                                >
                                    {drinkBaselines.reduce((sum, baseline) => sum + baseline || 0, 0)}
                                </Typography>
                            </Box>
                            <Box className={classes.summaryBox}>
                                <Typography
                                    variant={TypographyVariants.H5}
                                    className={`${classes.summaryText} ${hide ? classes.shiftDaysText : null}`}
                                >
                                    {'Total Weekly '}
                                    <span
                                        style={{
                                            fontWeight: TypographyWeights.BOLD
                                        }}
                                    >
                                        Dry Days
                                    </span>
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H2}
                                    className={`${classes.summaryValue} ${hide ? classes.fadeInDaysValue : null}`}
                                >
                                    {drinkBaselines.reduce((sum, baseline) => sum + (baseline === 0 ? 1 : 0), 0)}
                                </Typography>
                            </Box>
                        </Box>
                        {body1 && (
                            <Box className={hide ? classes.tipBox : classes.hidden}>
                                <Box className={classes.tipBodyBox}>
                                    <Typography
                                        variant={TypographyVariants.H6}
                                        className={classes.tipBody1}
                                    >
                                        {body1}
                                    </Typography>
                                    <Typography
                                        variant={TypographyVariants.H6}
                                    >
                                        {body2}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        <Typography
                            variant={TypographyVariants.H6}
                            className={`${classes.error} ${hide ? classes.hide : null}`}
                        >
                            {error}
                        </Typography>
                    </Box>
                    <Box
                        style={{
                            height: '800px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default DrinkBaselines2;
